"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.fetchProjectFeatures = exports.finishCreateProject = exports.getSalesRepresentive = exports.deleteMeta = exports.fetchMetas = exports.insertMeta = exports.handleRemoveUserFromGroup = exports.handleDeleteEditUserGroup = exports.handleDeleteEditCategory = exports.getProject = exports.searchBillingInformations = exports.createProject = exports.fetchPrintProviders = exports.fetchCustomers = exports.changeStep = void 0;
const t = require("io-ts");
const fp = require("fp-ts");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const monocle = require("monocle-ts");
const sok_types_1 = require("@caddigital/sok-types");
const ActionTypes = require("../../actionTypes/project/wizard");
const Actions = require("../../actions/project/wizard");
const ToastActions = require("../../actions/toast");
const Step1FeatureActions = require("../../actions/project/wizardSteps/step1_features");
const validation_1 = require("../../utils/validation");
const errorHandling_1 = require("../../utils/errorHandling");
const features_1 = require("../../utils/features");
const translation_1 = require("../../utils/translation");
const wizardLens = monocle.Lens.fromPath([
    'project',
    'wizard'
]);
const stepLens = (step) => new monocle.Optional((s) => s['step' + step] ? fp.option.some(s['step' + step]) : fp.option.none, 
/* istanbul ignore next */
(a) => (s) => s);
const activeStepLens = monocle.Lens.fromPath(['project', 'wizard', 'wizardStep']);
const validateStep = (step) => (store) => {
    const currentStep = activeStepLens.get(store);
    if (currentStep > step) {
        return fp.validation.success(true);
    }
    if (currentStep === 0) {
        return wizardLens
            .asOptional()
            .compose(stepLens(1))
            .getOption(store)
            .fold(fp.validation.failure([
            {
                key: 'any',
                value: 'Unhandled exception'
            }
        ]), (x) => {
            const first = fp.function.compose((0, validation_1.ap)((0, features_1.validateFeatures)(x.features)), (0, validation_1.ap)((0, validation_1.isNotNull)('customer')(x.customer)), (0, validation_1.ap)((0, validation_1.isNotNull)('projectStatus')(x.projectStatus)), (0, validation_1.ap)((0, validation_1.isNotNull)('projectType')(x.projectType)))((0, validation_1.isNotEmpty)('projectName')(x));
            const second = x.notifyFollowing.reduce((r, email) => {
                return (0, validation_1.ap)((0, validation_1.isEmail)('notifyFollowing')(email))(r);
            }, first);
            const third = x.projectAdmins.reduce((r, email) => {
                return (0, validation_1.ap)((0, validation_1.isEmail)('projectAdmins')(email))(r);
            }, second);
            // Validate printProvider if feature is enabled
            if (x.features.filter((feature) => feature.constant === sok_types_1.Feature.PRINT_ORDERS && feature.enabled).length > 0) {
                return (0, validation_1.ap)((0, validation_1.isNotUndefined)('selectedPrintProvider')(x.selectedPrintProvider))(third);
            }
            return third;
        });
    }
    return fp.validation.success(true);
};
function changeStep(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.CHANGE_STEP), (0, operators_1.mergeMap)((request) => validateStep(request.payload.wizardStep)(store.value).fold((e) => (0, rxjs_1.of)(Actions.validateStepFailure(e)), (_) => (0, rxjs_1.of)(Actions.validateStepSuccess(request.payload.wizardStep)))));
}
exports.changeStep = changeStep;
function fetchCustomers(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.FETCH_CUSTOMERS), (0, operators_1.mergeMap)(({ payload }) => ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        method: 'GET',
        url: `${baseUrl}api/customer/search/${payload.name}`
    })), (0, operators_1.mergeMap)(({ response }) => {
        const validate = t
            .array(t.interface({
            businessId: t.string,
            name: t.string,
            id: t.number
        }))
            .decode(response);
        const act = validate.fold((e) => Actions.fetchCustomersFailed((0, errorHandling_1.parseValidationError)(e)), (c) => {
            return Actions.fetchCustomersSuccess(c);
        });
        return (0, rxjs_1.of)(act);
    }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.fetchCustomersFailed((0, errorHandling_1.parseAjaxErrorResponse)(error)))));
}
exports.fetchCustomers = fetchCustomers;
function fetchPrintProviders(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.FETCH_PRINT_PROVIDER_REQUEST), (0, operators_1.mergeMap)(() => ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        method: 'GET',
        url: `${baseUrl}api/print-provider/`
    })), (0, operators_1.mergeMap)((res) => {
        const validate = t
            .array(sok_types_1.PrintProvider.iPrintProvider)
            .decode(res.response);
        const act = validate.fold((e) => Actions.fetchPrintProviderFailure((0, errorHandling_1.parseValidationError)(e)), (c) => {
            return Actions.fetchPrintProviderSuccess(c);
        });
        return (0, rxjs_1.of)(act);
    }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.fetchPrintProviderFailure((0, errorHandling_1.parseAjaxErrorResponse)(error)))));
}
exports.fetchPrintProviders = fetchPrintProviders;
function createProject(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.CREATE_PROJECT), (0, operators_1.mergeMap)(({ payload }) => validateStep(0)(store.value).fold((e) => (0, rxjs_1.of)(Actions.validateStepFailure(e)), () => {
        if (payload.projectStatus === null || payload.projectType === null) {
            return (0, rxjs_1.of)(Actions.createProjectFailed({
                type: 'VALIDATION',
                errors: ['project status or project type missing']
            }));
        }
        const body = {
            id: payload.id >= 0 ? payload.id : -1,
            name: payload.projectName,
            description: payload.description,
            projectStatus: payload.projectStatus,
            projectType: payload.projectType,
            customerUuid: payload.customer && payload.customer.uuid
                ? payload.customer.uuid
                : '',
            features: payload.features,
            salesRepresentive: payload.salesRepresentive,
            billingInformations: payload.billingInformations,
            notifyFollowing: payload.notifyFollowing,
            printProvider: payload.selectedPrintProvider
                ? payload.selectedPrintProvider.id
                : undefined,
            conversionService: payload.conversionService,
            notes: payload.notes,
            template: payload.template
        };
        return ajax({
            headers: {
                'Content-Type': 'application/json',
                Authorization: getToken()
            },
            method: payload.id === -1 ? 'POST' : 'PUT',
            body: body,
            url: `${baseUrl}api/admin/project/`
        }).pipe((0, operators_1.mergeMap)(({ response }) => {
            const validate = sok_types_1.Project.iExtendedProject.decode(response);
            const act = validate.fold((err) => Actions.createProjectFailed((0, errorHandling_1.parseValidationError)(err)), (x) => {
                return Actions.createProjectSuccess(x);
            });
            return (0, rxjs_1.of)(act);
        }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.createProjectFailed((0, errorHandling_1.parseAjaxErrorResponse)(error)))));
    })));
}
exports.createProject = createProject;
function searchBillingInformations(action$, _store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.SEARCH_BILLING_INFORMATION_REQUEST), (0, operators_1.mergeMap)((action) => ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        method: 'GET',
        url: `${baseUrl}api/admin/project/billing-information?${Object.entries(action.payload)
            .map((v) => v[1] !== undefined && [v[0], encodeURIComponent(v[1])].join('='))
            .join('&')}`
    }).pipe((0, operators_1.mergeMap)((res) => {
        const validate = t
            .array(sok_types_1.BillingInformation.iInternalBillingInformation)
            .decode(res.response);
        const act = validate.fold((e) => Actions.searchBillingInformationFailed(action.payload.type, (0, errorHandling_1.parseValidationError)(e)), (c) => Actions.searchBillingInformationSuccess(action.payload.type, c));
        return (0, rxjs_1.of)(act);
    }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.searchBillingInformationFailed(action.payload.type, (0, errorHandling_1.parseAjaxErrorResponse)(error)))))));
}
exports.searchBillingInformations = searchBillingInformations;
function getProject(action$, store, { ajax, getToken, baseUrl }) {
    return action$.ofType(ActionTypes.MODIFY_PROJECT_REQUEST).pipe((0, operators_1.mergeMap)((action) => ajax({
        headers: {
            Authorization: getToken()
        },
        method: 'GET',
        url: `${baseUrl}api/project/${action.payload.id}`
    }).pipe((0, operators_1.mergeMap)((res) => {
        const validate = sok_types_1.Project.iExtendedProject.decode(res.response);
        const act = validate.fold((y) => Actions.modifyProjectFailure((0, errorHandling_1.parseValidationError)(y)), (x) => Actions.setModifyProject(x));
        return (0, rxjs_1.of)(act);
    }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.modifyProjectFailure((0, errorHandling_1.parseAjaxErrorResponse)(error)))))));
}
exports.getProject = getProject;
function handleDeleteEditCategory(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.DELETE_EDIT_CATEGORY_REQUEST), (0, operators_1.mergeMap)((request) => {
        const projectId = store.value.project.wizard.step1.id;
        const body = Object.assign({ projectId: projectId }, (request.payload.mode === 'EDIT'
            ? { name: request.payload.name, categoryId: request.payload.id }
            : { categoryIds: request.payload.id }));
        return ajax({
            headers: {
                'Content-Type': 'application/json',
                Authorization: getToken()
            },
            method: request.payload.mode === 'DELETE' ? 'DELETE' : 'PUT',
            body: body,
            url: `${baseUrl}api/project/${projectId}/categories`
        }).pipe((0, operators_1.mergeMap)(() => {
            const act = [
                Actions.deleteEditCategorySuccess(),
                Actions.UsergroupList.fetchRequest('PROJECT', projectId || NaN)
            ];
            return (0, rxjs_1.of)(...act);
        }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.deleteEditCategoryFailed((0, errorHandling_1.parseAjaxErrorResponse)(error)))));
    }));
}
exports.handleDeleteEditCategory = handleDeleteEditCategory;
function handleDeleteEditUserGroup(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.DELETE_EDIT_USER_GROUP_REQUEST), (0, operators_1.mergeMap)((request) => {
        const projectId = store.value.project.wizard.step1.id;
        const body = Object.assign({ projectId: projectId }, (request.payload.mode === 'EDIT'
            ? { name: request.payload.name, userProfileId: request.payload.id }
            : { userProfileIds: request.payload.id }));
        return ajax({
            headers: {
                'Content-Type': 'application/json',
                Authorization: getToken()
            },
            method: request.payload.mode === 'DELETE' ? 'DELETE' : 'PUT',
            body: body,
            url: `${baseUrl}api/project/${projectId}/user-profiles`
        }).pipe((0, operators_1.mergeMap)(() => {
            const act = [
                Actions.deleteEditUserGroupSuccess(),
                Actions.UsergroupList.fetchRequest('PROJECT', projectId || NaN)
            ];
            return (0, rxjs_1.of)(...act);
        }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.deleteEditUserGroupFailed((0, errorHandling_1.parseAjaxErrorResponse)(error)))));
    }));
}
exports.handleDeleteEditUserGroup = handleDeleteEditUserGroup;
function handleRemoveUserFromGroup(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.REMOVE_USER_FROM_GROUP), (0, operators_1.mergeMap)((request) => {
        const projectId = store.value.project.wizard.step1.id;
        return ajax({
            headers: {
                'Content-Type': 'application/json',
                Authorization: getToken()
            },
            method: 'DELETE',
            body: {
                userId: request.payload.userId,
                groupId: request.payload.groupId,
                projectId: projectId
            },
            url: `${baseUrl}api/admin/project/user`
        }).pipe((0, operators_1.mergeMap)(() => {
            const act = [
                Actions.removeUserFromGroupSuccess(),
                Actions.UsergroupList.fetchRequest('PROJECT', projectId || NaN)
            ];
            return (0, rxjs_1.of)(...act);
        }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.removeUserFromGroupFailed((0, errorHandling_1.parseAjaxErrorResponse)(error)))));
    }));
}
exports.handleRemoveUserFromGroup = handleRemoveUserFromGroup;
function validateMeta(action) {
    const validation = action.payload.meta.reduce((r, meta) => {
        const val = (0, validation_1.isNotEmpty)('name')(meta);
        if (meta.type === 'select') {
            return (0, validation_1.ap)(val)((0, validation_1.isNotEmptyList)('options')(meta.options));
        }
        if (meta.type === 'dependant-select') {
            return fp.function.compose(...((options) => options.map((x) => (0, validation_1.ap)((0, validation_1.isNotEmptyList)(x.value)(x.options || []))))(meta.options), (0, validation_1.ap)((0, validation_1.isNotEmptyList)('options')(meta.options)))((0, validation_1.ap)((0, validation_1.isNotEmpty)('name')(meta.submeta))((0, validation_1.isNotEmpty)('name')(meta)));
        }
        return val;
    }, {});
    return validation;
}
function insertMeta(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.ADD_META_ELEMENT ||
        action.type === ActionTypes.MOVE_META), (0, operators_1.mergeMap)((action) => {
        const projectId = store.value.project.wizard.step1.id;
        return validateMeta(action).fold((e) => (0, rxjs_1.of)(Actions.insertMetaValidation(e)), () => ajax({
            headers: {
                'Content-Type': 'application/json',
                Authorization: getToken()
            },
            method: 'PUT',
            body: {
                schemaId: action.payload.schemaId,
                meta: action.payload.meta
            },
            url: `${baseUrl}api/admin/project/meta/`
        }).pipe((0, operators_1.mergeMap)(() => (0, rxjs_1.of)(...[
            Actions.fetchMetas(projectId),
            action.type === ActionTypes.ADD_META_ELEMENT
                ? Actions.insertMetaSuccess()
                : Actions.moveMetaSuccess()
        ])), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(action.type === ActionTypes.ADD_META_ELEMENT
            ? Actions.insertMetaFailed((0, errorHandling_1.parseAjaxErrorResponse)(error))
            : Actions.moveMetaFailed((0, errorHandling_1.parseAjaxErrorResponse)(error))))));
    }));
}
exports.insertMeta = insertMeta;
function fetchMetas(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.FETCH_METAS), (0, operators_1.mergeMap)((request) => ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        method: 'GET',
        url: `${baseUrl}api/project/${request.payload.projectId}/metadata`
    }).pipe((0, operators_1.mergeMap)(({ response }) => {
        const validation = sok_types_1.Project.iProjectMetadata.decode(response);
        const act = validation.fold((e) => Actions.fetchMetasFailed((0, errorHandling_1.parseValidationError)(e)), (e) => Actions.fetchMetasSuccess(e));
        return (0, rxjs_1.of)(act);
    }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.fetchMetasFailed((0, errorHandling_1.parseAjaxErrorResponse)(error)))))));
}
exports.fetchMetas = fetchMetas;
function deleteMeta(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.DELETE_META_ELEMENT), (0, operators_1.mergeMap)((request) => {
        return ajax({
            headers: {
                'Content-Type': 'application/json',
                Authorization: getToken()
            },
            method: 'DELETE',
            body: {
                metaUuid: request.payload.uuid,
                schemaId: request.payload.schemaId
            },
            url: `${baseUrl}api/admin/project/meta`
        }).pipe((0, operators_1.mergeMap)(() => (0, rxjs_1.of)(Actions.deleteMetaElementSuccess(request.payload.uuid))), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.deleteMetaElementFailed((0, errorHandling_1.parseAjaxErrorResponse)(error)))));
    }));
}
exports.deleteMeta = deleteMeta;
function getSalesRepresentive(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.GET_SALES_REPRESENTIVE_REQUEST), (0, operators_1.mergeMap)((request) => {
        return ajax({
            headers: {
                'Content-Type': 'application/json',
                Authorization: getToken()
            },
            method: 'GET',
            url: `${baseUrl}api/customer/${request.payload}`
        }).pipe((0, operators_1.mergeMap)(({ response }) => {
            const validation = sok_types_1.Customer.iExtendedCustomer.decode(response);
            const act = validation.fold((e) => Actions.getSalesRepresentiveFailure((0, errorHandling_1.parseValidationError)(e)), (e) => Actions.getSalesRepresentiveSuccess(e.salesRepresentative));
            return (0, rxjs_1.of)(act);
        }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.getSalesRepresentiveFailure((0, errorHandling_1.parseAjaxErrorResponse)(error)))));
    }));
}
exports.getSalesRepresentive = getSalesRepresentive;
function finishCreateProject(action$, store, { ajax, getToken, baseUrl }) {
    return action$.ofType(ActionTypes.step5Content.FINISH_PROJECT_CREATION).pipe((0, operators_1.mergeMap)(({ payload }) => ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        method: 'POST',
        url: `${baseUrl}api/admin/project/finish`,
        body: {
            projectId: payload.projectId
        }
    }).pipe((0, operators_1.mergeMap)(({ response }) => {
        const validation = t
            .interface({
            message: t.string
        })
            .decode(response);
        const act = validation.fold((e) => [
            Actions.step5.finishProjectCreationFailure((0, errorHandling_1.parseValidationError)(e)),
            ToastActions.addNewToast((0, translation_1.getLocalization)('project/wizard/finish/title'), (0, translation_1.getLocalization)('project/wizard/finish/failed'), 'FAILURE')
        ], (res) => [
            Actions.step5.finishProjectCreationSuccess(res.message),
            ToastActions.addNewToast((0, translation_1.getLocalization)('project/wizard/finish/title'), (0, translation_1.getLocalization)('project/wizard/finish/success'), 'NORMAL')
        ]);
        return (0, rxjs_1.of)(...act);
    }), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.step5.finishProjectCreationFailure((0, errorHandling_1.parseAjaxErrorResponse)(error)), ToastActions.addNewToast((0, translation_1.getLocalization)('project/wizard/finish/title'), (0, translation_1.getLocalization)('project/wizard/finish/failed'), 'FAILURE'))))));
}
exports.finishCreateProject = finishCreateProject;
function fetchProjectFeatures(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.step1_features.FETCH_FEATURES_REQUEST), (0, operators_1.mergeMap)((action) => ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        method: 'GET',
        url: `${baseUrl}api/admin/features/projects/${action.payload.uuid}`
    }).pipe((0, operators_1.mergeMap)(({ response }) => t
        .array(sok_types_1.Feature.iFeature)
        .decode(response)
        .fold((error) => (0, rxjs_1.of)(Step1FeatureActions.fetchFeaturesFailed((0, errorHandling_1.parseValidationError)(error))), (res) => (0, rxjs_1.of)(Step1FeatureActions.fetchFeaturesSuccess(res)))), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Step1FeatureActions.fetchFeaturesFailed((0, errorHandling_1.parseAjaxErrorResponse)(error)))))));
}
exports.fetchProjectFeatures = fetchProjectFeatures;
//# sourceMappingURL=wizard.js.map